import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../css/bulkUpload.css"; // 👈 add this import

export default function BulkItemCategoryUpload({ open, onClose, onCommitted }) {
  const modalRootRef = useRef(null);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [session, setSession] = useState(null);
  const [sampleErrors, setSampleErrors] = useState([]);
  const [stage, setStage] = useState("idle");
  const [busy, setBusy] = useState(false);

  const swalInModal = (opts) =>
    Swal.fire({
      target: modalRootRef.current || document.body,
      zIndex: 20000,
      ...opts,
    });

  useEffect(() => {
    if (!open) {
      setFile(null);
      setUploadId(null);
      setSession(null);
      setSampleErrors([]);
      setStage("idle");
      setBusy(false);
      if (fileRef.current) fileRef.current.value = null;
    }
  }, [open]);

  const API = "https://rfs2.infraconsoft.net.in/api";

  const downloadTemplate = () => window.open(`${API}/itemCat/bulk/template`, "_blank");
  const onPickFile = (e) => setFile(e.target.files?.[0] || null);

  const upload = async () => {
    if (!file) return swalInModal({ icon: "warning", title: "No file", text: "Please choose a CSV file." });
    setBusy(true); setStage("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/itemCat/bulk/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadId(data.upload_id);
      setStage("staged");
      swalInModal({ icon: "success", title: "Uploaded", text: `Staged ~${data.stagedRowsApprox} rows.` });
    } catch (e) {
      console.error(e);
      setStage("failed");
      swalInModal({ icon: "error", title: "Upload failed", text: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const validate = async () => {
    if (!uploadId) return swalInModal({ icon: "warning", title: "No upload", text: "Please upload a file first." });
    setBusy(true); setStage("validating");
    try {
      const { data } = await axios.post(`${API}/itemCat/bulk/validate`, null, { params: { upload_id: uploadId } });
      setSession(data.session || null);
      setSampleErrors(data.sampleErrors || []);
      setStage("validated");
      const total = data.session?.total_rows ?? 0;
      const valid = data.session?.valid_rows ?? 0;
      const invalid = data.session?.invalid_rows ?? 0;
      swalInModal({
        icon: invalid > 0 ? "info" : "success",
        title: "Validation complete",
        html: `
          <div style="text-align:left">
            <div><b>Total:</b> ${total}</div>
            <div><b>Valid:</b> ${valid}</div>
            <div><b>Invalid:</b> ${invalid}</div>
          </div>
        `,
      });
    } catch (e) {
      console.error(e);
      setStage("failed");
      swalInModal({ icon: "error", title: "Validation failed", text: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!uploadId) return swalInModal({ icon: "warning", title: "No upload", text: "Please upload & validate first." });
    setBusy(true); setStage("committing");
    try {
      const { data } = await axios.post(`${API}/itemCat/bulk/commit`, null, { params: { upload_id: uploadId } });
      setSession(data.session || null);
      setStage("committed");
      swalInModal({ icon: "success", title: "Committed", text: "Categories inserted successfully." });
      onCommitted?.();
    } catch (e) {
      console.error(e);
      setStage("failed");
      swalInModal({ icon: "error", title: "Commit failed", text: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const refreshStatus = async () => {
    if (!uploadId) return;
    try {
      const { data } = await axios.get(`${API}/itemCat/bulk/status`, { params: { upload_id: uploadId } });
      setSession(data.session || null);
      setSampleErrors(data.sampleErrors || []);
    } catch (e) {
      console.error(e);
      swalInModal({ icon: "error", title: "Status error", text: e?.response?.data?.error || e.message });
    }
  };

  const downloadErrors = () => {
    if (!uploadId) return;
    window.open(`${API}/itemCat/bulk/errors.csv?upload_id=${uploadId}`, "_blank");
  };

  if (!open) return null;

  return (
    <div ref={modalRootRef} className="bu-overlay">
      <div className="bu-card">
        <div className="bu-header">
          <div className="bu-title">
            Bulk Upload — Item Categories
          </div>
          <button onClick={onClose} className="bu-close" aria-label="Close">✕</button>
        </div>

        <div className="bu-body">
          <div className="bu-info">
            <div className="bu-note">
              CSV header required: <code>itemName</code>
            </div>
            <button className="bu-template-btn" onClick={downloadTemplate}>Download Template</button>
          </div>

          <div className="bu-upload">
            <div className="bu-input-row">
              <input type="file" ref={fileRef} onChange={onPickFile} accept=".csv" className="bu-file" disabled={busy} />
            </div>
            <div className="bu-file-note">Select a .csv file with the first column named <b>itemName</b>.</div>

            <div className="bu-actions">
              <button className="btn" onClick={upload} disabled={busy || !file}>
                {stage === "uploading" ? "Uploading…" : "Upload & Stage"}
              </button>
              <button className="btn" onClick={validate} disabled={busy || !uploadId}>
                {stage === "validating" ? "Validating…" : "Validate"}
              </button>
              <button className="btn" onClick={refreshStatus} disabled={busy || !uploadId}>
                Refresh Status
              </button>
              <button
                className="btn btn-primary"
                onClick={commit}
                disabled={busy || !uploadId || (session && session.invalid_rows > 0)}
                title={session?.invalid_rows > 0 ? "Fix or ignore invalid rows before committing." : ""}
              >
                {stage === "committing" ? "Committing…" : "Commit Valid Rows"}
              </button>
            </div>
          </div>

          {uploadId && (
            <div className="bu-status">
              <div className="bu-status-row">
                <span className="bu-pill"><b>Upload ID:</b> {uploadId}</span>
                <span className="bu-pill"><b>Status:</b> {session?.status || stage}</span>
              </div>
              <div className="bu-status-row">
                <span className="bu-pill"><b>Total:</b> {session?.total_rows ?? "-"}</span>
                <span className="bu-pill"><b>Valid:</b> {session?.valid_rows ?? "-"}</span>
                <span className="bu-pill"><b>Invalid:</b> {session?.invalid_rows ?? "-"}</span>
              </div>
            </div>
          )}

          {sampleErrors?.length > 0 && (
            <div className="bu-errors">
              <div className="bu-errors-head">
                <h4 style="margin:0;font-size:1rem;">Top Errors (first 50)</h4>
                <button className="btn" onClick={downloadErrors} disabled={!uploadId}>Download All Errors</button>
              </div>
              <div className="bu-table-wrap">
                <table className="bu-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleErrors.map((e, idx) => (
                      <tr key={idx}>
                        <td>{e.rownum}</td>
                        <td>{e.error_msg}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
